import { PlusIcon } from "@xola/icons";
import { Spinner } from "@xola/ui-kit";
import { compact, sortBy } from "lodash";
import React, { useMemo } from "react";
import { CollectionNode, CollectionNodeObject, ProductObject } from "types";
import { SecondaryButton } from "../Common/SecondaryButton";
import { CollectionImage } from "@/components/Product/GlobalSettings/Collections/CollectionImage";
import { MultipleParentCollectionSelector } from "@/components/Product/GlobalSettings/Collections/ParentCollectionSelector";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { getSeller } from "@/utils/getSeller";

interface ProductCollectionSelectorProps {
    addedCollectionPaths: string[];
    isLoading: boolean;
    value: string[];
    isOpen: boolean;
    onChange: (collections: string[]) => void;
    onOpenChange: (open: boolean) => void;
}

const getCollectionIdFromPath = (path: string) => {
    const pathSegments = path.split("/").filter(Boolean);
    return pathSegments[pathSegments.length - 1];
};

export const ProductCollectionSelector = ({
    addedCollectionPaths,
    isLoading,
    value,
    isOpen,
    onChange,
    onOpenChange,
}: ProductCollectionSelectorProps) => {
    const { data: parentCollections, isLoading: isLoadingParentCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        { object: CollectionNodeObject.GROUP_COLLECTION_NODE },
    );

    const handleChange = (newPaths: string[]) => {
        onChange(newPaths);
    };

    const sortedItems = useMemo(() => {
        const collectionById = new Map(parentCollections.map((c) => [c.id, c]));

        return sortBy(
            compact(
                value.map((path) => {
                    const collectionId = getCollectionIdFromPath(path);
                    const collection = collectionById.get(collectionId);

                    if (!collection) return null;

                    return {
                        path,
                        collection,
                        sortKey: (collection.breadcrumb ?? collection.name ?? "").toLowerCase(),
                    };
                }),
            ),
            (x) => x.sortKey,
        );
    }, [value, parentCollections]);

    return (
        <>
            <div className="space-y-2">
                <div className="flex items-center justify-between pb-1">
                    <span className="text-sm font-semibold">Parent Collection</span>
                    <SecondaryButton
                        size="small"
                        className="flex items-center gap-1"
                        icon={<PlusIcon />}
                        onClick={() => onOpenChange(true)}
                    >
                        Add Collection
                    </SecondaryButton>
                </div>

                {isLoading || isLoadingParentCollections ? (
                    <div className="flex items-center justify-center py-4">
                        <Spinner size="small" />
                    </div>
                ) : value.length === 0 ? (
                    <div className="text-sm text-gray-dark">No collections selected</div>
                ) : (
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                        {sortedItems.map(({ path, collection }) => (
                            <div key={path} className="flex items-center gap-2">
                                <CollectionImage collection={collection} size="tiny" />
                                <span className="text-sm">{collection.breadcrumb ?? collection.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="h-0 overflow-hidden">
                <MultipleParentCollectionSelector
                    enableDepthCheck={false}
                    value={value.length > 0 ? value : addedCollectionPaths}
                    isOpen={isOpen}
                    onChange={handleChange}
                    onOpenChange={onOpenChange}
                />
            </div>
        </>
    );
};

interface ProductCollectionPayloadProps {
    newPaths: string[];
    oldPaths: string[];
    addedCollections: CollectionNode[];
    productId: string;
    productObject?: ProductObject;
}

export const getProductCollectionPayload = ({
    newPaths,
    oldPaths,
    addedCollections,
    productId,
    productObject,
}: ProductCollectionPayloadProps) => {
    const sellerId = getSeller().id;
    const pathsToDelete = oldPaths.filter((path) => !newPaths.includes(path));
    const pathsToCreate = newPaths.filter((path) => !oldPaths.includes(path));

    const collectionOperations: any[] = [];
    let nextOpId = 0;

    // Delete operations - delete all product_collection_node entries for removed paths
    for (const path of pathsToDelete) {
        const collectionNodes = addedCollections.filter((node) => node.path === path);
        for (const collectionNode of collectionNodes) {
            collectionOperations.push({
                id: String(nextOpId++),
                op: "delete",
                data: {
                    id: collectionNode.id,
                },
            });
        }
    }

    // Create operations
    for (const path of pathsToCreate) {
        collectionOperations.push({
            id: String(nextOpId++),
            op: "create",
            data: {
                object: "product_collection_node",
                product: {
                    id: productId,
                    ...(productObject ? { object: productObject } : {}),
                },
                seller: {
                    id: sellerId,
                },
                path: path,
            },
        });
    }

    return collectionOperations;
};
